import { UserCircleIcon, XIcon } from "@heroicons/react/outline";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { patchData } from "../backend";
import { useModal, WorkflowContext } from "../contexts";
import useUser from "../hooks/useUser";
import { Review } from "../interfaces";
import { ORDINAL_MAP } from "../utils";
import Avatar from "./Avatar";
import PayWallModalTeams from "./PayWallModalTeams";
import SettingsTeamModalInvite from "./SettingsTeamModalInvite";
import SmallSelectField from "./SmallSelectField";
import SmallTextInputField from "./SmallTextInputField";
import TransitionWorkflowPage from "./transitions/TransitionWorkflowPage";
import TransitionWorkflowPageContent from "./transitions/TransitionWorkflowPageContent";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import WorkflowModalShare from "./WorkflowModalShare";
import ToggleButton from "./ToggleButton";

function WorkflowApprovals() {
  const [isSaving, setIsSaving] = useState(false);

  const { user } = useUser();
  const { openModal } = useModal();

  const { t } = useTranslation();
  const { hasPermission } = useUser();
  const { workflow, mutateWorkflow } = useContext(WorkflowContext);

  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();

  const [review, setReview] = useState(workflow!.review!);
  const [approvalUserIds, setApprovalUserIds] = useState<string[]>(review?.approvalUserIds ?? []);

  const [inviteEmail, setInviteEmail] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  function handleCheckOnChange(value: boolean, userId: string) {
    if (!workflow.team)
      return openModal(<WorkflowModalShare workflow={workflow} />);

    setHasChanges(true);

    if (value)
      setApprovalUserIds(ids => [...ids, userId])
    else
      setApprovalUserIds(ids => ids.filter(id => id !== userId))
  }

  function updateReview(updates: Partial<Review>) {
    setHasChanges(true);
    setReview(prev => ({ ...prev!, ...updates }));
  }

  function handleChangePosition(value: string, userId: string) {
    setHasChanges(true);

    const index = Number(value);

    const newIds = approvalUserIds.filter(id => id !== userId);
    newIds.splice(index, 0, userId);

    setApprovalUserIds([...newIds]);
  }

  async function handleSave() {
    setIsSaving(true);

    await patchData(`/workflows/reviews/${workflow.review!.id}/`, { ...review, approvalUserIds })
      .then(() => mutateWorkflow())
      .catch(console.error)
      .finally(() => { setIsSaving(false); setHasChanges(false); })
  }

  async function handleInvite() {
    if (!user?.activeTeam)
      return openModal(<PayWallModalTeams />);

    openModal(<SettingsTeamModalInvite initialEmail={inviteEmail} />);
    setInviteEmail("");
  }

  function openUpgradeModal() {
    if (!user?.activeTeam)
      return openModal(<PayWallModalTeams />);

    return openModal(<WorkflowModalShare workflow={workflow} />);
  }

  const emailIsValid = /[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9.-]{2,}/.test(inviteEmail);

  const isReviewOnly = !workflow.team;
  const hasApprovalUsers = approvalUserIds.length > 0;

  const sortedApprovalUsers = user?.activeTeam?.members.sort((a, b) => {
    if (a.id === user?.id) return -1;
    if (b.id === user?.id) return 1;

    if (!review?.approvalsInSequence) return 0;

    const aIndex = approvalUserIds.indexOf(a.id);
    const bIndex = approvalUserIds.indexOf(b.id);

    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });

  return (
    <TransitionWorkflowPage show={location.pathname.endsWith("/approvals")}>
      <TransitionWorkflowPageSideBar>
        <div className="flex flex-col gap-4">
          <div className="font-semibold">
            {t("Settings")}
          </div>

          {isReviewOnly &&
            <div className="flex flex-col gap-2">
              <div className="text-xs font-semibold text-gray-400">{t("Options")}</div>
              <div className="flex items-center text-sm justify-between text-gray-600 font-semibold">
                {t("Request approvals from team members")}
                <ToggleButton value={false} onChange={() => openUpgradeModal()} />
              </div>
              <div className="flex items-center text-sm justify-between text-gray-600 font-semibold">
                {t("Notify me via email")}
                <ToggleButton value={review?.approvalsNotify} onChange={(approvalsNotify) => updateReview({ approvalsNotify })} />
              </div>
            </div>
          }

          {!isReviewOnly &&
            <div className="flex flex-col gap-2">
              <div className="text-xs font-semibold text-gray-400">
                {t("Approval Order")}
              </div>
              <div className="flex items-center gap-4 w-full">
                <div className={`flex flex-col w-full items-center justify-center rounded border p-4 text-sm font-semibold text-gray-600 cursor-pointer ${!!review?.approvalsInSequence ? "outline outline-blue" : ""}`} onClick={() => updateReview({ approvalsInSequence: true, approvalsRequireAll: true, approvalsNotify: true })}>
                  <img className="w-5 h-5" src="https://static.portant.co/icon-sequential.svg" alt="Seqential icon" />
                  {t("Sequential")}
                </div>
                <div className={`flex flex-col w-full items-center justify-center rounded border p-4 text-sm font-semibold text-gray-600 cursor-pointer ${!review?.approvalsInSequence ? "outline outline-blue" : ""}`} onClick={() => updateReview({ approvalsInSequence: false })}>
                  <img className="w-5 h-5" src="https://static.portant.co/icon-parallel.svg" alt="Parallel icon" />
                  {t("Parallel")}
                </div>
              </div>
              <div className="flex flex-col gap-2 mt-4">
                <div className="text-xs font-semibold text-gray-400">{t("Options")}</div>
                <div className="flex items-center text-sm justify-between text-gray-600 font-semibold">
                  {t("All selected members must approve")}
                  <ToggleButton value={review?.approvalsRequireAll || !!review?.approvalsInSequence} onChange={(approvalsRequireAll) => updateReview({ approvalsRequireAll })} disabled={!!review?.approvalsInSequence} />
                </div>
                <div className="flex items-center text-sm justify-between text-gray-600 font-semibold">
                  {t("Notify via email")}
                  <ToggleButton value={!!review?.approvalsNotify || !!review?.approvalsInSequence} onChange={(approvalsNotify) => updateReview({ approvalsNotify })} disabled={!!review?.approvalsInSequence} />
                </div>
              </div>
            </div>
          }
          {(review?.approvalsNotify || !!review?.approvalsInSequence) &&
            <div className="flex flex-col gap-2">
              <div className="h-px w-full bg-gray-300 flex-shrink-0 my-2" />
              <div className="text-xs font-semibold text-gray-400">{t("Email Message")}</div>
              <textarea className="w-full p-2 border border-gray-300 rounded resize-none text-sm" rows={1} value={review.approvalsSubject} placeholder={t("Subject")} onChange={(e) => updateReview({ approvalsSubject: e.target.value })} />
              <textarea className="w-full h-24 p-2 border border-gray-300 rounded resize-none text-sm" value={review.approvalsMessage} placeholder={t("Message")} onChange={(e) => updateReview({ approvalsMessage: e.target.value })} />
            </div>
          }
          <button className="btn btn-blue text-sm " onClick={handleSave} type="button" disabled={hasPermission(workflow.team, "workflows.edit") && (isSaving || !hasChanges) && (!isReviewOnly || hasApprovalUsers)}>{isSaving ? t("Saving...") : t("Save")}</button>
          <div className="text-xs text-gray-600 mr-8">
            {t("Once documents have been generated they will not continue to be processed until they have been reviewed and approved.")}
          </div>
        </div>
      </TransitionWorkflowPageSideBar>

      <TransitionWorkflowPageContent className="relative">
        <XIcon className="absolute top-4 right-4 w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
        <div className="flex flex-col w-[800px] h-full mx-auto mt-10">
          <div className="font-gilroy font-semibold text-3xl mb-4">
            {workflow.team ? t("Request Approvals") : t("Review Documents")}
          </div>
          {(isReviewOnly && !user?.activeTeam) &&
            <div className="bg-blue w-full h-20 text-white flex items-center justify-between px-10 rounded-lg mb-4" style={{ background: "linear-gradient(180deg, #0E43DC 0%, #026AE7 33%, #0080E3 67%, #A1CDF1 100%)" }}>
              {t("Upgrade to teams to add multiple approvers to your workflow.")}
              <button className="text-sm bg-black text-white px-4 py-1 rounded-lg font-gilroy font-semibold hover:bg-gray-900 transition-colors w-fit" onClick={openUpgradeModal}>
                {t("Upgrade to Teams")}
              </button>
            </div>
          }
          {(isReviewOnly && !!user?.activeTeam) &&
            <div className="bg-blue w-full h-20 text-white flex items-center justify-between px-10 rounded-lg mb-4" style={{ background: "linear-gradient(180deg, #0E43DC 0%, #026AE7 33%, #0080E3 67%, #A1CDF1 100%)" }}>
              {t("Share the workflow with your team to unlock multiple approvers")}
              <button className="text-sm bg-black text-white px-4 py-1 rounded-lg font-gilroy font-semibold hover:bg-gray-900 transition-colors w-fit" onClick={openUpgradeModal}>
                {t("Share with Team")}
              </button>
            </div>
          }
          <div className="text-xs text-gray-600 font-semibold mb-1">
            {isReviewOnly
              ? t("Review documents yourself or add team members and unlock multiple approvers")
              : t("Request approvals from team members")
            }
          </div>
          <div className={`flex overflow-y-auto gap-2 py-2 border-b border-t w-full ${isReviewOnly ? "flex-col" : "flex-col"}`} style={{ maxHeight: "calc(100vh - 400px)" }}>
            <div className={`flex items-center gap-2 text-sm ${isReviewOnly ? "opacity-50" : "opacity-100"}`}>
              <input type="checkbox" className="h-4 w-4" checked={approvalUserIds.includes("__runner")} onChange={(e) => handleCheckOnChange(e.target.checked, "__runner")} disabled={isReviewOnly} />
              <UserCircleIcon className="h-8 w-8 flex-shrink-0 text-gray-400" />
              <div className="flex flex-col text-xs">
                <p className="font-gilroy font-bold whitespace-nowrap overflow-hidden text-ellipsis text-gray-600">
                  {t("Workflow Runner")}
                  <span className="text-blue"> ({t("Dynamic")})</span>
                </p>
                <p className="text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis">
                  {t("The person who initiated the automation")}
                </p>
              </div>
              {(!!review?.approvalsInSequence && approvalUserIds.includes("__runner")) &&
                <SmallSelectField className="ml-auto text-xs text-gray-400 w-16" value={String(approvalUserIds.indexOf("__runner"))} onChange={(value) => handleChangePosition(value, "__runner")}>
                  <>
                    {Array.from({ length: approvalUserIds.length }).fill(0).map((_, index) =>
                      <option key={String(index)} value={String(index)}>{t(ORDINAL_MAP[index + 1])}</option>
                    )}
                  </>
                </SmallSelectField>
              }
            </div>
            {(sortedApprovalUsers ?? [workflow.owner!]).map((teamUser) =>
              <div key={teamUser.id} className={`flex items-center gap-2 text-sm ${(isReviewOnly && teamUser.id !== workflow.owner?.id) ? "opacity-50" : "opacity-100"}`}>
                <input type="checkbox" className="h-4 w-4" checked={(!isReviewOnly && approvalUserIds.includes(teamUser.id)) || (isReviewOnly && teamUser.id === user?.id)} onChange={(e) => handleCheckOnChange(e.target.checked, teamUser.id)} disabled={(isReviewOnly && !(user?.activeTeam && teamUser.id !== user?.id)) || (!approvalUserIds.includes(teamUser.id) && teamUser.teamRole === "VIEWER")} />
                <Avatar url={teamUser.avatar} className="h-8 w-8 flex-shrink-0" />
                <div className="flex flex-col text-xs">
                  <p className="font-gilroy font-bold whitespace-nowrap overflow-hidden text-ellipsis text-gray-600">
                    {teamUser.fullName}
                    {teamUser.teamRole === "VIEWER" &&
                      <span className="text-gray-400"> ({t("Viewer")})</span>
                    }
                  </p>
                  <p className="text-gray-400 whitespace-nowrap overflow-hidden text-ellipsis">{teamUser.email}</p>
                </div>
                {(!!review?.approvalsInSequence && approvalUserIds.includes(teamUser.id)) &&
                  <SmallSelectField className="ml-auto text-xs text-gray-400 w-16" value={String(approvalUserIds.indexOf(teamUser.id))} onChange={(value) => handleChangePosition(value, teamUser.id)}>
                    <>
                      {Array.from({ length: approvalUserIds.length }).fill(0).map((_, index) =>
                        <option key={String(index)} value={String(index)}>{t(ORDINAL_MAP[index + 1])}</option>
                      )}
                    </>
                  </SmallSelectField>
                }
              </div>
            )}
          </div>
          <div className="flex items-end gap-2 text-sm mt-2">
            <SmallTextInputField className="w-full" value={inviteEmail} label="Email" placeholder={t("Add another approver")} onChange={(value) => setInviteEmail(value)} />
            <button className="btn btn-blue py-[5px]" onClick={handleInvite} disabled={!emailIsValid}>{t("Invite")}</button>
          </div>
        </div>

      </TransitionWorkflowPageContent>
    </TransitionWorkflowPage>
  );

}

export default WorkflowApprovals;
